import * as ReactDOM from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import './alize.css';
import App from './app/app';
import { PdfWindowViewer } from './app/features/chat/components/PdfWindowViewer';
import { ChatbotProviders } from './app/providers/ChatbotProviders';
import AuthBoundary from './auth/AuthBoundary';
import { OktaCallback } from './auth/OktaCallback';
import { applyAlizeBranding } from './lib/applyAlizeBranding';
import {
  initDatadogRUM,
  setUserWithAdGuid,
  setStaticRegion,
  setAppMode,
} from './lib/datadog';
import {
  fixMaterialSymbolFont,
  unblockMaterialSymbolIcons,
} from './lib/fixMaterialSymbolFont';
import './material-symbols-compat.css';
import './standalone-root.css';

// Guards against a second standalone mount (e.g. the legacy GPTPlugin.init()
// embed path in app/lib/plugin.ts calling initApp() alongside the module-load
// auto-run below). Two mounts each create their own OktaAuthProvider/OktaAuth
// instance, and both race to consume the single PKCE codeVerifier stored for
// the /login/callback redirect - the loser throws "Could not load PKCE
// codeVerifier from storage".
let initialized = false;

export function initApp() {
  if (initialized) {
    console.log(
      '[Chatbot] initApp() already ran - skipping duplicate standalone initialization',
    );
    return;
  }

  // Detect if running as MFE
  const isMfe = document.getElementById('chatbot-mfe-container') !== null;

  // Skip standalone initialization if running as MFE
  if (isMfe) {
    console.log(
      '[Chatbot] Running as MFE - skipping standalone initialization',
    );
    return;
  }

  initialized = true;
  console.log('[Chatbot] Running in standalone mode');

  initDatadogRUM();
  setUserWithAdGuid();
  setStaticRegion();
  setAppMode('standalone');

  const rootElement = document.getElementById('root');
  if (!rootElement) throw new Error('Root element not found');
  // Scopes alize-ui/Tailwind's compiled CSS (rewritten at build time to nest
  // under `.chatbot-mfe-scope`, see vite.config.ts) onto this element instead
  // of relying on the `#chatbot-mfe-container` id, which doesn't exist in
  // standalone mode - see applyAlizeBranding.ts's own doc comment.
  rootElement.classList.add('chatbot-mfe-scope');
  const root = ReactDOM.createRoot(rootElement);

  // Pass the same element that received `.chatbot-mfe-scope` above, so the
  // theme/collection attributes land where the scoped CSS can see them - see
  // applyAlizeBranding's own doc comment.
  applyAlizeBranding(rootElement);
  fixMaterialSymbolFont();
  unblockMaterialSymbolIcons();

  root.render(
    <ChatbotProviders Router={BrowserRouter}>
      <AuthBoundary>
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar
          closeOnClick
        />
        <Routes>
          <Route path="/" element={<App />} />
          <Route path="/ICMemoGPT" element={<App />} />
          <Route path="/login/callback" element={<OktaCallback />} />
          <Route path="/pdf-window-viewer" element={<PdfWindowViewer />} />
        </Routes>
      </AuthBoundary>
    </ChatbotProviders>,
  );
}

// Initialize the app in standalone mode (not MFE)
if (document.getElementById('chatbot-mfe-container') === null) {
  initApp();
}
